import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function translateDocumentType(t, documentType) {
  if (!documentType) return "";
  switch (documentType) {
    case "WORK_PERMIT":
      return t("userProfile.documentTypeWorkPermit");
    case "OBSERVATION":
      return t("userProfile.documentTypeObservation");
    default:
      return documentType;
  }
}

function translateVisibilityType(t, visibilityType) {
  if (!visibilityType) return "";
  const key = `editUser.visibility${visibilityType.replace(/_/g, "")}`;
  return t(key, visibilityType);
}

function SecurityTab({ userDetails, onChangePassword, onChangeAvatar }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid container spacing={{ xs: 3, md: 4 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {t("userProfile.accountSecurity", "Account Security")}
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Card
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: "16px !important" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t("userProfile.passwordSecurity", "Password")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {t(
                        "userProfile.passwordDescription",
                        "Update your password to keep your account secure"
                      )}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<LockIcon />}
                    onClick={onChangePassword}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {t("userProfile.changePasswordButton")}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: "16px !important" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t("userProfile.profilePicture", "Profile Picture")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {t(
                        "userProfile.profilePictureDescription",
                        "Update your profile picture"
                      )}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={onChangeAvatar}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {t("userProfile.changeAvatarButton")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SecurityIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {t("userProfile.documentVisibilityTitle")}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {userDetails?.document_visibility_infos?.map((setting) => (
              <Grid key={setting.id} size={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    {translateDocumentType(t, setting?.document_type)}
                  </Typography>
                  <Chip
                    label={translateVisibilityType(t, setting.visibility_type)}
                    variant="filled"
                    color="primary"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}

SecurityTab.propTypes = {
  userDetails: PropTypes.object,
  onChangePassword: PropTypes.func.isRequired,
  onChangeAvatar: PropTypes.func.isRequired,
};

export default SecurityTab;
